import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// Reward user with cash
export const rewardUserWithCash = createAsyncThunk(
  'reward/rewardUserWithCash',
  async ({ amount, inviteCode, reason }, { rejectWithValue }) => {
    try {
      console.log('💰 Initiating cash reward. Amount:', amount, 'Invite Code:', inviteCode, 'Reason:', reason);

      const response = await axios.post(`${API_BASE_URL}/api/rewards/cash`, {
        amount,
        inviteCode,
        reason
      });

      console.log('✅ Cash reward successful:', response.data);

      return response.data;
    } catch (error) {
      console.error('❌ Failed to reward user with cash:', error);

      return rejectWithValue(
        error.response?.data || { error: 'Unable to process cash reward' }
      );
    }
  }
);

// Reward user with coins
export const rewardUserWithCoin = createAsyncThunk(
  'reward/rewardUserWithCoin',
  async ({ amount, inviteCode, reason }, { rejectWithValue }) => {
    try {
      console.log('🪙 Initiating coin reward. Amount:', amount, 'Invite Code:', inviteCode, 'Reason:', reason);

      const response = await axios.post(`${API_BASE_URL}/api/rewards/coin`, {
        amount,
        inviteCode,
        reason
      });

      console.log('✅ Coin reward successful:', response.data);

      return response.data;
    } catch (error) {
      console.error('❌ Failed to reward user with coins:', error);

      return rejectWithValue(
        error.response?.data || { error: 'Unable to process coin reward' }
      );
    }
  }
);

const rewardSlice = createSlice({
  name: 'reward',
  initialState: {
    loading: false,
    error: null,
    lastReward: null,
    rewardHistory: [],
    successMessage: null
  },
  reducers: {
    clearRewardState: (state) => {
      state.loading = false;
      state.error = null;
      state.lastReward = null;
      state.successMessage = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    addToRewardHistory: (state, action) => {
      state.rewardHistory.unshift(action.payload);
      // Keep only last 50 rewards in history
      if (state.rewardHistory.length > 50) {
        state.rewardHistory = state.rewardHistory.slice(0, 50);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Reward with Cash
      .addCase(rewardUserWithCash.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(rewardUserWithCash.fulfilled, (state, action) => {
        state.loading = false;
        state.lastReward = action.payload;
        state.successMessage = 'User rewarded with cash successfully!';
        state.rewardHistory.unshift({
          ...action.payload.reward,
          timestamp: new Date().toISOString(),
          type: 'cash'
        });
      })
      .addCase(rewardUserWithCash.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to reward user with cash';
      })
      
      // Reward with Coins
      .addCase(rewardUserWithCoin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(rewardUserWithCoin.fulfilled, (state, action) => {
        state.loading = false;
        state.lastReward = action.payload;
        state.successMessage = 'User rewarded with coins successfully!';
        state.rewardHistory.unshift({
          ...action.payload.reward,
          timestamp: new Date().toISOString(),
          type: 'coin'
        });
      })
      .addCase(rewardUserWithCoin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to reward user with coins';
      });
  },
});

export const { 
  clearRewardState, 
  clearError, 
  clearSuccessMessage, 
  addToRewardHistory 
} = rewardSlice.actions;

// Selectors
export const selectRewardLoading = (state) => state.reward.loading;
export const selectRewardError = (state) => state.reward.error;
export const selectLastReward = (state) => state.reward.lastReward;
export const selectRewardHistory = (state) => state.reward.rewardHistory;
export const selectSuccessMessage = (state) => state.reward.successMessage;

export default rewardSlice.reducer; 